import { Controller } from "@hotwired/stimulus";

// Navigates a table row from a click on its linked cells instead of wrapping each cell in an anchor,
// so the cell text can be drag-selected and copied. Modifier/middle clicks open a new tab.
const DRAG_THRESHOLD_PX = 3;
const INTERACTIVE = "a, button, input, select, textarea, label, summary, [role=button]";

export default class extends Controller {
  static values = { href: String };

  mousedown(event) {
    // Middle-click would otherwise engage autoscroll on Windows/Linux now that the cell is not an anchor.
    if (event.button === 1) event.preventDefault();
    this.mousedownPoint = { x: event.clientX, y: event.clientY };
  }

  visit(event) {
    if (this.#ignorable(event)) return;

    if (event.type === "auxclick" || event.metaKey || event.ctrlKey || event.shiftKey) {
      this.#openInNewTab();
    } else {
      window.location.assign(this.hrefValue);
    }
  }

  keydown(event) {
    if (event.key === "Enter" && event.target === event.currentTarget) {
      window.location.assign(this.hrefValue);
    }
  }

  #openInNewTab() {
    const anchor = document.createElement("a");
    anchor.href = this.hrefValue;
    anchor.target = "_blank";
    anchor.rel = "noopener";
    anchor.click();
  }

  #ignorable(event) {
    const expectedButton = event.type === "auxclick" ? 1 : 0;
    if (event.button !== expectedButton) return true;
    if (event.defaultPrevented || event.target.closest(INTERACTIVE)) return true;

    // A drag that leaves text selected in the row is a copy gesture, not a navigation.
    return this.#dragged(event) && this.#hasSelectionInRow();
  }

  #dragged(event) {
    if (!this.mousedownPoint) return false;
    const { x, y } = this.mousedownPoint;
    return Math.hypot(event.clientX - x, event.clientY - y) > DRAG_THRESHOLD_PX;
  }

  #hasSelectionInRow() {
    const selection = window.getSelection();
    return Boolean(selection && !selection.isCollapsed && this.element.contains(selection.anchorNode));
  }
}
